import React, { useState } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { 
  Mail, 
  Phone, 
  Linkedin, 
  Github, 
  MapPin, 
  Briefcase,
  ChevronDown,
  Award,
  Code,
  Database,
  BarChart3,
  Menu,
  X
} from 'lucide-react';
import { personalInfo, aboutData, experienceData, skillsData, cvData } from './mock';

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setMobileMenuOpen(false); // Close mobile menu after clicking
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">{personalInfo.name}</div>
          
          <div className="nav-links desktop-nav">
            <button 
              onClick={() => scrollToSection('home')} 
              className={`nav-link ${activeSection === 'home' ? 'active' : ''}`}
            >
              Home
            </button>
            <button 
              onClick={() => scrollToSection('about')} 
              className={`nav-link ${activeSection === 'about' ? 'active' : ''}`}
            >
              About
            </button>
            <button 
              onClick={() => scrollToSection('experience')} 
              className={`nav-link ${activeSection === 'experience' ? 'active' : ''}`}
            >
              Experience
            </button>
            <button 
              onClick={() => scrollToSection('skills')} 
              className={`nav-link ${activeSection === 'skills' ? 'active' : ''}`}
            >
              Skills
            </button>
			<button 
              onClick={() => scrollToSection('cv')} 
              className={`nav-link ${activeSection === 'cv' ? 'active' : ''}`}
            >
              CV
            </button>
            <button 
              onClick={() => scrollToSection('contact')} 
              className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`}
            >
              Contact
            </button>
          </div>

          <button 
            className="mobile-menu-button" 
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <div className={`mobile-nav ${mobileMenuOpen ? 'open' : ''}`}>
          <button 
            onClick={() => scrollToSection('home')} 
            className={`nav-link-mobile ${activeSection === 'home' ? 'active' : ''}`}
          >
            Home
          </button>
          <button 
            onClick={() => scrollToSection('about')} 
            className={`nav-link-mobile ${activeSection === 'about' ? 'active' : ''}`}
          >
            About
          </button>
          <button 
            onClick={() => scrollToSection('experience')} 
            className={`nav-link-mobile ${activeSection === 'experience' ? 'active' : ''}`}
          >
            Experience
          </button>
          <button 
            onClick={() => scrollToSection('skills')} 
            className={`nav-link-mobile ${activeSection === 'skills' ? 'active' : ''}`}
          >
            Skills
          </button>
          <button 
            onClick={() => scrollToSection('contact')} 
            className={`nav-link-mobile ${activeSection === 'contact' ? 'active' : ''}`}
          >
            Contact
          </button>
        </div>
      </nav>

      <section id="home" className="hero-section">
        <div className="hero-content">
          <div className="hero-image-container">
            <img 
              src={personalInfo.profileImage} 
              alt={personalInfo.name}
              className="profile-image"
            />
          </div>
          <h1 className="hero-title">{personalInfo.name}</h1>
          <h2 className="hero-subtitle">{personalInfo.title}</h2>
          <p className="hero-tagline">{personalInfo.tagline}</p>
          <div className="hero-location">
            <MapPin size={16} />
            <span>{personalInfo.location}</span>
          </div>
          <Button 
            onClick={() => scrollToSection('contact')} 
            className="hero-cta"
            size="lg"
          >
            Get In Touch
          </Button>
          <button 
            onClick={() => scrollToSection('about')} 
            className="scroll-indicator"
            aria-label="Scroll to about section"
          >
            <ChevronDown size={24} className="bounce" />
          </button>
        </div>
      </section>

      <section id="about" className="section about-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">About Me</h2>
            <Separator className="title-separator" />
          </div>
          <div className="about-content">
            <p className="about-summary">{aboutData.summary}</p>
            <div className="about-highlights">
              <h3 className="highlights-title">
                <Award size={20} />
                Career Highlights
              </h3>
              <ul className="highlights-list">
                {aboutData.highlights.map((highlight, index) => (
                  <li key={index} className="highlight-item">
                    {highlight}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section id="experience" className="section experience-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Professional Experience</h2>
            <Separator className="title-separator" />
          </div>
          <div className="experience-timeline">
            {experienceData.map((exp) => (
              <Card key={exp.id} className="experience-card">
                <CardContent className="experience-card-content">
                  <div className="experience-header">
                    <div className="experience-icon">
                      <Briefcase size={24} />
                    </div>
                    <div className="experience-info">
                      <h3 className="experience-role">{exp.role}</h3>
                      <h4 className="experience-company">{exp.company}</h4>
                      <div className="experience-meta">
                        <span className="experience-period">{exp.period}</span>
                        <span className="experience-location">{exp.location}</span>
                      </div>
                    </div>
                  </div>
                  <ul className="experience-responsibilities">
                    {exp.responsibilities.map((resp, index) => (
                      <li key={index} className="responsibility-item">
                        {resp}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="skills" className="section skills-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Technical Skills</h2>
            <Separator className="title-separator" />
          </div>
          <div className="skills-grid">
            <Card className="skill-category-card">
              <CardContent className="skill-category-content">
                <div className="skill-category-header">
                  <Code size={24} className="skill-icon" />
                  <h3 className="skill-category-title">Languages</h3>
                </div>
                <div className="skills-badges">
                  {skillsData.languages.map((skill, index) => (
                    <Badge key={index} variant="secondary" className="skill-badge">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="skill-category-card">
              <CardContent className="skill-category-content">
                <div className="skill-category-header">
                  <Database size={24} className="skill-icon" />
                  <h3 className="skill-category-title">Databases</h3>
                </div>
                <div className="skills-badges">
                  {skillsData.databases.map((skill, index) => (
                    <Badge key={index} variant="secondary" className="skill-badge">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="skill-category-card">
              <CardContent className="skill-category-content">
                <div className="skill-category-header">
                  <BarChart3 size={24} className="skill-icon" />
                  <h3 className="skill-category-title">Tools & Frameworks</h3>
                </div>
                <div className="skills-badges">
                  {skillsData.tools.map((skill, index) => (
                    <Badge key={index} variant="secondary" className="skill-badge">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="skill-category-card">
              <CardContent className="skill-category-content">
                <div className="skill-category-header">
                  <Code size={24} className="skill-icon" />
                  <h3 className="skill-category-title">Data Frameworks</h3>
                </div>
                <div className="skills-badges">
                  {skillsData.frameworks.map((skill, index) => (
                    <Badge key={index} variant="secondary" className="skill-badge">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="skill-category-card">
              <CardContent className="skill-category-content">
                <div className="skill-category-header">
                  <Database size={24} className="skill-icon" />
                  <h3 className="skill-category-title">Cloud Platforms</h3>
                </div>
                <div className="skills-badges">
                  {skillsData.cloud.map((skill, index) => (
                    <Badge key={index} variant="secondary" className="skill-badge">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
	  
	  {/* Know More / CV Section */}
      <section id="cv" className="section cv-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">{cvData.title}</h2>
            <Separator className="title-separator" />
          </div>
      
          <div className="cv-card">
            <p className="cv-description">
              {cvData.description}
            </p>
      
            <a
              href={cvData.filePath}
              target="_blank"
              rel="noopener noreferrer"
              className="cv-link"
            >
              {cvData.label}
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section contact-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get In Touch</h2>
            <Separator className="title-separator" />
          </div>
          <div className="contact-content">
            <p className="contact-intro">
              I'm always interested in hearing about new opportunities and collaborations.
              Feel free to reach out through any of the channels below.
            </p>
            <div className="contact-grid">
              <a href={`mailto:${personalInfo.email}`} className="contact-item">
                <div className="contact-icon">
                  <Mail size={24} />
                </div>
                <div className="contact-details">
                  <span className="contact-label">Email</span>
                  <span className="contact-value">{personalInfo.email}</span>
                </div>
              </a>

              <a href={`tel:${personalInfo.mobile}`} className="contact-item">
                <div className="contact-icon">
                  <Phone size={24} />
                </div>
                <div className="contact-details">
                  <span className="contact-label">Mobile</span>
                  <span className="contact-value">{personalInfo.mobile}</span>
                </div>
              </a>

              <a 
                href={personalInfo.linkedin} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="contact-item"
              >
                <div className="contact-icon">
                  <Linkedin size={24} />
                </div>
                <div className="contact-details">
                  <span className="contact-label">LinkedIn</span>
                  <span className="contact-value">Connect with me</span>
                </div>
              </a>

              <a 
                href={personalInfo.github} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="contact-item"
              >
                <div className="contact-icon">
                  <Github size={24} />
                </div>
                <div className="contact-details">
                  <span className="contact-label">GitHub</span>
                  <span className="contact-value">View my work</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-container">
          <p className="footer-text">
            © 2025 {personalInfo.name}. All rights reserved.
          </p>
          <div className="footer-social">
            <a href={personalInfo.linkedin} target="_blank" rel="noopener noreferrer" className="footer-social-link">
              <Linkedin size={20} />
            </a>
            <a href={personalInfo.github} target="_blank" rel="noopener noreferrer" className="footer-social-link">
              <Github size={20} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
